'use client'
import { RegisterAndLoginAction } from '@/actions'
import { ButtonRoundStyle, Form, RHFTextField } from '@/components'
import { CALLBACK_URL_KEY } from '@/constants'
import { setRef } from '@/features/ref/utils/setRef'
import { useAccountStore, useCartStore, useCompareStore } from '@/store'
import { DEFAULT_LOGIN_REDIRECT } from '@/utils/routes'
import yup from '@/utils/yup-global'
import { registerSchema } from '@/validations'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useEffect, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

interface IRegisterFormProps {
  verifiedIdentifier?: string
  identifierType?: 'email' | 'phone'
}

/**
 * Form component cho việc đăng ký tài khoản mới
 * Bao gồm các field: firstname, lastname, email, mobile_phone, password
 * Sau khi đăng ký thành công sẽ tự động đăng nhập và redirect user
 */
export const RegisterForm = ({ verifiedIdentifier, identifierType }: IRegisterFormProps) => {
  const [isPending, startTransition] = useTransition()
  const t = useTranslations('UserProfile')
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get(CALLBACK_URL_KEY)

  // Store hooks để update user data sau login (giống reset-password-form)
  const { setUser } = useAccountStore()
  const { mergeCart } = useCartStore()
  const { assignToUser } = useCompareStore()

  const method = useForm<yup.InferType<typeof registerSchema>>({ resolver: yupResolver(registerSchema) })
  const { handleSubmit, setValue } = method

  // Pre-fill và disable field đã được verify
  useEffect(() => {
    if (verifiedIdentifier && identifierType) {
      if (identifierType === 'email') {
        setValue('email', verifiedIdentifier)
      } else {
        setValue('mobile_phone', verifiedIdentifier)
      }
    }
  }, [verifiedIdentifier, identifierType, setValue])

  const onSubmit = (data: yup.InferType<typeof registerSchema>) => {
    startTransition(async () => {
      try {
        // Thực hiện đăng ký và auto login (giống reset-password flow)
        const result = await RegisterAndLoginAction(data, callbackUrl)

        if (result.status === 'error') {
          toast.error(result.message)
          return
        }
        // Cập nhật thông tin user và merge data giống như login bình thường
        setUser(result?.data ?? {})
        mergeCart()
        assignToUser()

        const redirectUrl = callbackUrl ?? DEFAULT_LOGIN_REDIRECT
        const refValue = result?.data?.increment_id
        const redirectUrlWithRef = setRef(refValue, redirectUrl)
        // router.push(redirectUrlWithRef)
        window.location.href = redirectUrlWithRef
      } catch (error) {
        console.error('Register error:', error)
      }
    })
  }

  return (
    <Form methods={method} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <RHFTextField
          name="lastname"
          label={t('Họ và tên của bạn')}
          placeholder={t('Nhập họ và tên của bạn')}
          disabled={isPending}
          required
        />
        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
          <RHFTextField
            name="email"
            type="email"
            label={t('E-mail')}
            placeholder={t('Nhập e-mail của bạn')}
            disabled={isPending || identifierType === 'email'}
            required
            helperText={
              identifierType === 'email' ? (
                <Typography component={'span'} color="success" fontSize={'0.75rem'}>
                  {t('Email đã được xác thực')}
                </Typography>
              ) : undefined
            }
          />
          <RHFTextField
            name="mobile_phone"
            type="tel"
            label={t('Số điện thoại')}
            placeholder={t('Nhập số điện thoại của bạn')}
            disabled={isPending || identifierType === 'phone'}
            required
            helperText={
              identifierType === 'phone' ? (
                <Typography component={'span'} color="success" fontSize={'0.75rem'}>
                  {t('Số điện thoại đã được xác thực')}
                </Typography>
              ) : undefined
            }
          />
        </Stack>
        <RHFTextField
          type="password"
          name="password"
          label={t('Mật khẩu')}
          required
          disabled={isPending}
          placeholder={t('Nhập mật khẩu của bạn')}
        />
        <RHFTextField
          type="password"
          name="confirmPassword"
          label={t('Xác nhận mật khẩu')}
          required
          disabled={isPending}
          placeholder={t('Nhập lại mật khẩu của bạn')}
        />
        <ButtonRoundStyle variant="contained" type="submit" disabled={isPending} loading={isPending}>
          {t('Tạo tài khoản')}
        </ButtonRoundStyle>
      </Stack>
    </Form>
  )
}
